import api from '@/lib/axios'
import type {
  ShiftReportConfig,
  ShiftReportFormResponse,
  ShiftReportLeaderReports,
  ShiftReportPhotoRef,
  ShiftReportMe,
  ShiftReportMyPerformance,
  ShiftReportRange,
  ShiftReportReview,
  ShiftReportSubmitBody
} from '@/types/shiftReports'

// Shift Reports add-on. Leader endpoints return form definitions and scores only;
// answers and photos come back only from the admin review endpoints.
const BASE = '/api/shift-reports'

export const shiftReportService = {
  /** Nav/routing probe. Never 403s. */
  getMe: async (): Promise<ShiftReportMe> => {
    const { data } = await api.get(`${BASE}/me`, { suppressErrorToast: true, suppressAutoReload: true })
    return data
  },

  /** Omitted params use the server defaults (today in store time, daypart for "now"). */
  getForm: async (reportDate?: string, daypartId?: string): Promise<ShiftReportFormResponse> => {
    const params: Record<string, string> = {}
    if (reportDate) params.reportDate = reportDate
    if (daypartId) params.daypartId = daypartId
    // A refetch (e.g. returning from the camera) must never reload the app and wipe
    // the leader's drafts on a 5xx; the form renders its own error state.
    const { data } = await api.get(`${BASE}/form`, { params, suppressErrorToast: true, suppressAutoReload: true })
    return data
  },

  /** Returns the S3 key + its upload sig only — leaders never get a URL back for their photos. */
  uploadPhoto: async (file: Blob): Promise<ShiftReportPhotoRef> => {
    const body = new FormData()
    body.append('photo', file instanceof File ? file : new File([file], 'photo.jpg', { type: file.type || 'image/jpeg' }))
    const { data } = await api.post(`${BASE}/photo`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000
    })
    return { key: data.key, sig: data.sig }
  },

  /** 400s carry a human message; callers show it themselves. */
  submit: async (body: ShiftReportSubmitBody): Promise<{ ok: true }> => {
    const { data } = await api.post(BASE, body, { suppressErrorToast: true, suppressAutoReload: true })
    return data
  },

  getMyPerformance: async (range: ShiftReportRange): Promise<ShiftReportMyPerformance> => {
    const { data } = await api.get(`${BASE}/my-performance`, {
      params: { range },
      suppressErrorToast: true,
      suppressAutoReload: true
    })
    return data
  },

  // --- admin ---
  getConfig: async (): Promise<ShiftReportConfig> => {
    // The settings section renders its own error state; never reload the app over unsaved edits.
    const { data } = await api.get(`${BASE}/config`, { suppressErrorToast: true, suppressAutoReload: true })
    return data
  },

  saveConfig: async (cfg: ShiftReportConfig): Promise<ShiftReportConfig> => {
    const { data } = await api.put(`${BASE}/config`, cfg, { suppressErrorToast: true, suppressAutoReload: true })
    return data
  },

  /** start/end are YYYY-MM-DD, inclusive, at most 93 days. */
  getReview: async (start: string, end: string): Promise<ShiftReportReview> => {
    const { data } = await api.get(`${BASE}/review`, { params: { start, end }, suppressErrorToast: true, suppressAutoReload: true })
    return data
  },

  getLeaderReports: async (userId: string, start: string, end: string): Promise<ShiftReportLeaderReports> => {
    const { data } = await api.get(`${BASE}/review/leader/${encodeURIComponent(userId)}`, {
      params: { start, end },
      suppressErrorToast: true,
      suppressAutoReload: true
    })
    return data
  }
}
